import { PAGINATION } from '@/constants/pagination.constant';
import { MessageType } from '@/types/common';
import { createAppSlice } from '../createAppSlice';
import { transformerArray } from '../transformer';
import { Message } from '@/enums/message.enum';
import { MESSAGE } from '@/constants/message.constant';

type PhysicalActivitySliceState = {
  loading: boolean;
  listPhysicalActivity: PhysicalActivity[];
  message?: MessageType;
  pagination: Pagination;
  paginationPhysicalActivityByIntensity: Pagination;
  timeLinePhysicalActivity?: string;
  showPopup?: boolean;
  sortPhysicalActivityLoading?: boolean;
  fitnessLevelList: FitnessLevel[];
  listTypePhysicalActivity: TypePhysicalActivity[];
  listAgePhysicalActivity: AgePhysicalActivity[];
  listIntensityPhysicalActivity: IntensityPhysicalActivity[];
  listSuitableObjectPhysicalActivity: SuitableObjectPhysicalActivity[]
  listPhysicalActivityByIntensity: PhysicalActivityByIntensity[],
  listDetailPhysicalActivityByIntensity?: DetailPhysicalActivityByIntensity
};

const initialState: PhysicalActivitySliceState = {
  loading: false,
  listPhysicalActivity: [],
  pagination: PAGINATION,
  paginationPhysicalActivityByIntensity: PAGINATION,
  showPopup: false,
  sortPhysicalActivityLoading: false,
  fitnessLevelList: [],
  listTypePhysicalActivity: [],
  listAgePhysicalActivity: [],
  listIntensityPhysicalActivity: [],
  listSuitableObjectPhysicalActivity: [],
  listPhysicalActivityByIntensity: [],
};
export const physicalActivitySlice = createAppSlice({
  name: 'physicalActivity',
  initialState,
  reducers: (create) => ({
    getListPhysicalActivity: create.asyncThunk(
      async (body?: Record<string, any>) => {
        const response = await apiFetcher.post('physical-activity-job/get-list-filter', body);
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listPhysicalActivity = transformerArray(action.payload.listData);
          state.pagination = action.payload.paging;
          state.timeLinePhysicalActivity = action.payload.timeSliderPhysicalActivity;
        },
        rejected: (state) => {
          state.loading = false;
          state.listPhysicalActivity = [];
          state.pagination = PAGINATION;
        }
      }
    ),
    getListFitnessLevel: create.asyncThunk(
      async () => {
        const response = await apiFetcher.get(
          `option/get-list-by-type`
        );
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.fitnessLevelList = transformerArray(action.payload);
        },
        rejected: (state) => {
          state.loading = false;
          state.fitnessLevelList = [];
        }
      }
    ),
    savePhysicalActivitySlider: create.asyncThunk(
      async (body: PhysicalActivity) => {
        const transformedBody = {
          ID: body.id,
          NAME: body.name,
          AGE_MIN: body.ageMin,
          AGE_MAX: body.ageMax,
          ID_OPTIONS: body.idOptions,
        };
        const response = await apiFetcher.post('physical-activity-job', transformedBody);
        return response;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state) => {
          state.loading = false;
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.CREATE_PHYSICAL_ACTIVITY_SUCCESS
          };
        },
        rejected: (state) => {
          state.loading = false;
          state.message = {
            type: Message.ERROR,
            message: MESSAGE.CREATE_PHYSICAL_ACTIVITY_FAIL
          };
        }
      }
    ),
    deletePhysicalActivity: create.asyncThunk(
      async (id: number) => {
        await apiFetcher.delete(`physical-activity-job/delete-job/?id=${id}`);
        return id;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listPhysicalActivity = state.listPhysicalActivity.filter(
            (activity) => activity.id !== String(action.payload)
          );
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.DELETE_PHYSICAL_ACTIVITY_SUCCESS
          };
        },
        rejected: (state) => {
          state.loading = false;
          state.message = {
            type: Message.ERROR,
            message: MESSAGE.DELETE_PHYSICAL_ACTIVITY_FAIL
          };
        }
      }
    ),
    getListTypePhysicalActivity: create.asyncThunk(
      async () => {
        const response = await apiFetcher.get(`physical-activity/get-list-type`);
        return response.data;
      },
      {
        fulfilled: (state, action) => {
          state.listTypePhysicalActivity = transformerArray(action.payload);
        },
        rejected: (state) => {
          state.listTypePhysicalActivity = [];
        }
      }
    ),
    getListAgePhysicalActivity: create.asyncThunk(
      async () => {
        const response = await apiFetcher.get(`age-ranges?type=PHYSICAL_ACTIVITY`);
        return response.data;
      },
      {
        fulfilled: (state, action) => {
          state.listAgePhysicalActivity = action.payload.listData;
        },
        rejected: (state) => {
          state.listAgePhysicalActivity = [];
        }
      }
    ),
    getListSuitableObjectPhysicalActivity: create.asyncThunk(
      async ({ type, option }: { type?: string; option?: string }) => {
        const response = await apiFetcher.get(`health-status-bmi?type=${type}&option=${option}`);
        return response.data;
      },
      {
        fulfilled: (state, action) => {
          state.listSuitableObjectPhysicalActivity = transformerArray(action.payload.listData);
        },
        rejected: (state) => {
          state.listSuitableObjectPhysicalActivity = [];
        }
      }
    ),
    getListIntensityPhysicalActivity: create.asyncThunk(
      async () => {
        const response = await apiFetcher.get(`option/get-list-by-type`);
        return response.data;
      },
      {
        fulfilled: (state, action) => {
          state.listIntensityPhysicalActivity = transformerArray(action.payload);
        },
        rejected: (state) => {
          state.listIntensityPhysicalActivity = [];
        }
      }
    ),
    getListPhysicalActivitiesByIntensity: create.asyncThunk(
      async (
        payload?: { customFilter?: FilterPhysicalActivityByIntensity; limit?: number; page?: number }
      ) => {
        const { customFilter, limit = 10, page = 1 } = payload || {};
        const response = await apiFetcher.post(`physical-activity/filter?page=${page}&limit=${limit}`, customFilter);
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listPhysicalActivityByIntensity = action.payload.listData;
          state.paginationPhysicalActivityByIntensity = action.payload.paging
        },
        rejected: (state) => {
          state.loading = false;
          state.listPhysicalActivityByIntensity = [];
          state.paginationPhysicalActivityByIntensity = PAGINATION;
        }
      }
    ),
    deletePhysicalActivityByIntensity: create.asyncThunk(
      async (id: number) => {
        await apiFetcher.delete(`physical-activity/delete/${id}`);
        return id;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listPhysicalActivityByIntensity = state.listPhysicalActivityByIntensity.filter(
            (activity) => activity.ID !== action.payload
          );
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.DELETE_PHYSICAL_ACTIVITY_BY_INTENSITY_SUCCESS
          };
        },
        rejected: (state) => {
          state.loading = false;
          state.message = {
            type: Message.ERROR,
            message: MESSAGE.UPDATE_PHYSICAL_ACTIVITY_INTENSITY_FAIL
          };
        }
      }
    ),
    createPhysicalActivityByIntensity: create.asyncThunk(
      async (body: FormData) => {
        const response = await apiFetcher.post('physical-activity/create', body,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state) => {
          state.loading = false;
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.CREATE_PHYSICAL_ACTIVITY_INTENSITY_SUCCESS
          };
        },
        rejected: (state) => {
          state.loading = false;
          state.message = {
            type: Message.ERROR,
            message: MESSAGE.CREATE_PHYSICAL_ACTIVITY_INTENSITY_FAIL
          };
        }
      }
    ),
    updatePhysicalActivityByIntensity: create.asyncThunk(
      async ({ id, payload }: { id: number; payload: FormData }) => {
        const response = await apiFetcher.post(`physical-activity/update/${id}`, payload,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state) => {
          state.loading = false;
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.UPDATE_PHYSICAL_ACTIVITY_INTENSITY_SUCCESS
          };
        },
        rejected: (state) => {
          state.loading = false;
          state.message = {
            type: Message.ERROR,
            message: MESSAGE.UPDATE_PHYSICAL_ACTIVITY_INTENSITY_FAIL
          };
        }
      }
    ),
    getDetailPhysicalActivityByIntensity: create.asyncThunk(
      async (id: number) => {
        const response = await apiFetcher.get(`physical-activity/detail/${id}`);
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listDetailPhysicalActivityByIntensity = action.payload
        },
        rejected: (state) => {
          state.loading = false;
          state.listDetailPhysicalActivityByIntensity = undefined;
        }
      }
    ),
    resetListSuitableObjectPhysicalActivity: create.reducer((state) => {
      state.listSuitableObjectPhysicalActivity = [];
    }),
    resetMessages: create.reducer((state) => {
      state.message = undefined;
    }),
  }),
  selectors: {
    watchListPhysicalActivity: (physicalActivity) => physicalActivity.listPhysicalActivity,
    watchListFitnessLevel: (physicalActivity) => physicalActivity.fitnessLevelList,
    watchPhysicalActivityMessage: (physicalActivity) => physicalActivity.message,
    watchPhysicalActivityPagination: (physicalActivity) => physicalActivity.pagination,
    watchPhysicalActivityLoading: (physicalActivity) => physicalActivity.loading,
    watchTypePhysicalActivity: (physicalActivity) => physicalActivity.listTypePhysicalActivity,
    watchAgePhysicalActivity: (physicalActivity) => physicalActivity.listAgePhysicalActivity,
    watchIntensityPhysicalActivity: (physicalActivity) => physicalActivity.listIntensityPhysicalActivity,
    watchSuitableObjectPhysicalActivity: (physicalActivity) => physicalActivity.listSuitableObjectPhysicalActivity,
    watchListPhysicalActivityByIntensity: (physicalActivity) => physicalActivity.listPhysicalActivityByIntensity,
    watchListDetailPhysicalActivityByIntensity: (physicalActivity) => physicalActivity.listDetailPhysicalActivityByIntensity,
    watchPaginationPhysicalActivityByIntensity: (physicalActivity) => physicalActivity.paginationPhysicalActivityByIntensity,
  }
});

export const {
  watchListPhysicalActivity,
  watchListFitnessLevel,
  watchTypePhysicalActivity,
  watchAgePhysicalActivity,
  watchIntensityPhysicalActivity,
  watchSuitableObjectPhysicalActivity,
  watchPhysicalActivityMessage,
  watchPhysicalActivityPagination,
  watchPhysicalActivityLoading,
  watchListPhysicalActivityByIntensity,
  watchListDetailPhysicalActivityByIntensity,
  watchPaginationPhysicalActivityByIntensity
} = physicalActivitySlice.selectors;

export const {
  getListPhysicalActivity,
  savePhysicalActivitySlider,
  getListFitnessLevel,
  getListTypePhysicalActivity,
  getListAgePhysicalActivity,
  getListIntensityPhysicalActivity,
  getListSuitableObjectPhysicalActivity,
  deletePhysicalActivity,
  getListPhysicalActivitiesByIntensity,
  deletePhysicalActivityByIntensity,
  createPhysicalActivityByIntensity,
  getDetailPhysicalActivityByIntensity,
  updatePhysicalActivityByIntensity,
  resetListSuitableObjectPhysicalActivity,
  resetMessages,
} = physicalActivitySlice.actions;
